import React, { useState, useMemo } from "react";
import {
  MONTH_NAMES,
  DAY_NAMES_SHORT,
  getDaysInMonth,
  getFirstDayOfMonth,
  getEntriesForMonth,
  formatDate,
  isToday,
  MOODS,
  type DiaryEntry,
} from "../api/mockData";

interface CalendarProps {
  onDaySelect: (dateStr: string) => void;
}

export function Calendar({ onDaySelect }: CalendarProps) {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());

  const entries = useMemo(
    () => getEntriesForMonth(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentYear(currentYear - 1);
      setCurrentMonth(11);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentYear(currentYear + 1);
      setCurrentMonth(0);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const goToToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  const getMoodColor = (entry: DiaryEntry | undefined): string | null => {
    if (!entry) return null;
    const mood = MOODS.find((m) => m.value === entry.mood);
    return mood?.softColor || null;
  };

  const getMoodEmoji = (entry: DiaryEntry | undefined): string | null => {
    if (!entry) return null;
    const mood = MOODS.find((m) => m.value === entry.mood);
    return mood?.emoji || null;
  };

  const isCurrentMonth =
    currentYear === today.getFullYear() && currentMonth === today.getMonth();

  // Build calendar grid cells
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Fill remaining to complete the row
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="animate-page-in">
      {/* Header */}
      <div className="px-5 pt-6 pb-3">
        <h1
          className="font-serif text-[2rem] font-extralight tracking-tight leading-tight"
          style={{ color: "var(--foreground)" }}
        >
          Дневник
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--muted-foreground)" }}>
          Записывайте свои мысли и настроение
        </p>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between px-5 py-3">
        <button
          onClick={prevMonth}
          className="w-9 h-9 flex items-center justify-center rounded-full transition-colors hover:bg-secondary"
          style={{ color: "var(--muted-foreground)" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <button
          onClick={goToToday}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors hover:bg-secondary"
        >
          <span className="font-sans font-medium text-base" style={{ color: "var(--foreground)" }}>
            {MONTH_NAMES[currentMonth]}
          </span>
          <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>
            {currentYear}
          </span>
          {!isCurrentMonth && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
              style={{
                background: "var(--primary)",
                color: "var(--primary-foreground)",
              }}
            >
              ·
            </span>
          )}
        </button>

        <button
          onClick={nextMonth}
          className="w-9 h-9 flex items-center justify-center rounded-full transition-colors hover:bg-secondary"
          style={{ color: "var(--muted-foreground)" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 px-4 mb-1">
        {DAY_NAMES_SHORT.map((name) => (
          <div
            key={name}
            className="text-center text-[11px] font-medium py-1.5 uppercase tracking-widest"
            style={{ color: "var(--muted-foreground)" }}
          >
            {name}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 px-4 gap-y-1">
        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} className="aspect-square" />;
          }

          const dateStr = formatDate(new Date(currentYear, currentMonth, day));
          const entry = entries[dateStr];
          const todayFlag = isToday(dateStr);
          const moodBg = getMoodColor(entry);
          const moodEmoji = getMoodEmoji(entry);
          const isFuture = new Date(currentYear, currentMonth, day) > today;

          return (
            <button
              key={dateStr}
              onClick={() => !isFuture && onDaySelect(dateStr)}
              disabled={isFuture}
              className="aspect-square flex flex-col items-center justify-center rounded-xl relative transition-all duration-200 group"
              style={{
                background: moodBg || (todayFlag ? "oklch(0.55 0.12 30 / 0.06)" : "transparent"),
                opacity: isFuture ? 0.3 : 1,
                cursor: isFuture ? "default" : "pointer",
              }}
            >
              <span
                className="text-[13px] font-medium transition-transform duration-150 group-hover:scale-110"
                style={{
                  color: todayFlag && !entry
                    ? "var(--primary)"
                    : entry
                    ? "var(--foreground)"
                    : "var(--foreground)",
                  fontWeight: todayFlag ? 600 : 400,
                }}
              >
                {day}
              </span>
              {moodEmoji && (
                <span className="text-[11px] leading-none mt-[-1px]">{moodEmoji}</span>
              )}
              {todayFlag && !entry && (
                <div
                  className="absolute bottom-1.5 w-1 h-1 rounded-full"
                  style={{ background: "var(--primary)" }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Today's quick action */}
      <div className="px-5 pt-5 pb-2">
        <button
          onClick={() => onDaySelect(formatDate(today))}
          className="w-full py-3.5 px-5 rounded-2xl flex items-center gap-3 transition-all duration-200 hover:scale-[0.99] active:scale-[0.97]"
          style={{
            background: "var(--primary)",
            color: "var(--primary-foreground)",
          }}
        >
          <span className="text-xl">✏️</span>
          <div className="text-left">
            <div className="text-sm font-medium">Как прошёл ваш день?</div>
            <div className="text-xs opacity-70">Нажмите, чтобы добавить запись</div>
          </div>
        </button>
      </div>

      {/* Recent entries */}
      <RecentEntries onDaySelect={onDaySelect} />
    </div>
  );
}

function RecentEntries({ onDaySelect }: { onDaySelect: (dateStr: string) => void }) {
  const entries = useMemo(() => {
    const all = Object.values(
      getEntriesForMonth(new Date().getFullYear(), new Date().getMonth())
    );
    return all.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);
  }, []);

  if (entries.length === 0) return null;

  return (
    <div className="px-5 pt-3 pb-28">
      <h3
        className="text-xs font-medium uppercase tracking-widest mb-3"
        style={{ color: "var(--muted-foreground)" }}
      >
        Недавние записи
      </h3>
      <div className="space-y-2 stagger-children">
        {entries.map((entry) => {
          const mood = MOODS.find((m) => m.value === entry.mood);
          return (
            <button
              key={entry.date}
              onClick={() => onDaySelect(entry.date)}
              className="w-full text-left p-3.5 rounded-xl transition-all duration-200 hover:scale-[0.99]"
              style={{ background: "var(--card)", border: "1px solid var(--border)" }}
            >
              <div className="flex items-start gap-3">
                <span className="text-xl">{mood?.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium" style={{ color: "var(--muted-foreground)" }}>
                      {new Date(entry.date + "T00:00:00").toLocaleDateString("ru-RU", {
                        day: "numeric",
                        month: "long",
                      })}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: mood?.color }}>
                      {mood?.label}
                    </span>
                  </div>
                  <p
                    className="text-sm mt-1 line-clamp-2 font-serif"
                    style={{ color: "var(--foreground)", lineHeight: "1.5" }}
                  >
                    {entry.text}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
